import { useState, useRef, useEffect } from "react";
import { Plane, MapPin, Loader2 } from "lucide-react";

// Comprehensive airport list
const AIRPORTS = [
  { city: "London", code: "LHR", name: "Heathrow", country: "UK" },
  { city: "London", code: "LGW", name: "Gatwick", country: "UK" },
  { city: "London", code: "STN", name: "Stansted", country: "UK" },
  { city: "London", code: "LTN", name: "Luton", country: "UK" },
  { city: "Berlin", code: "BER", name: "Brandenburg", country: "Germany" },
  { city: "Amsterdam", code: "AMS", name: "Schiphol", country: "Netherlands" },
  { city: "Paris", code: "CDG", name: "Charles de Gaulle", country: "France" },
  { city: "Paris", code: "ORY", name: "Orly", country: "France" },
  { city: "Barcelona", code: "BCN", name: "El Prat", country: "Spain" },
  { city: "Madrid", code: "MAD", name: "Barajas", country: "Spain" },
  { city: "Ibiza", code: "IBZ", name: "Ibiza Airport", country: "Spain" },
  { city: "Manchester", code: "MAN", name: "Manchester Airport", country: "UK" },
  { city: "Dublin", code: "DUB", name: "Dublin Airport", country: "Ireland" },
  { city: "Brussels", code: "BRU", name: "Brussels Airport", country: "Belgium" },
  { city: "Munich", code: "MUC", name: "Munich Airport", country: "Germany" },
  { city: "Frankfurt", code: "FRA", name: "Frankfurt Airport", country: "Germany" },
  { city: "Vienna", code: "VIE", name: "Vienna Airport", country: "Austria" },
  { city: "Zurich", code: "ZRH", name: "Zurich Airport", country: "Switzerland" },
  { city: "Milan", code: "MXP", name: "Malpensa", country: "Italy" },
  { city: "Rome", code: "FCO", name: "Fiumicino", country: "Italy" },
  { city: "Lisbon", code: "LIS", name: "Lisbon Airport", country: "Portugal" },
  { city: "Copenhagen", code: "CPH", name: "Copenhagen Airport", country: "Denmark" },
  { city: "Stockholm", code: "ARN", name: "Arlanda", country: "Sweden" },
  { city: "Oslo", code: "OSL", name: "Gardermoen", country: "Norway" },
  { city: "Prague", code: "PRG", name: "Václav Havel", country: "Czech Republic" },
  { city: "Budapest", code: "BUD", name: "Budapest Airport", country: "Hungary" },
  { city: "Warsaw", code: "WAW", name: "Chopin Airport", country: "Poland" },
  { city: "New York", code: "JFK", name: "John F. Kennedy", country: "USA" },
  { city: "New York", code: "EWR", name: "Newark", country: "USA" },
  { city: "Los Angeles", code: "LAX", name: "Los Angeles International", country: "USA" },
  { city: "Miami", code: "MIA", name: "Miami International", country: "USA" },
  { city: "Edinburgh", code: "EDI", name: "Edinburgh Airport", country: "UK" },
  { city: "Glasgow", code: "GLA", name: "Glasgow Airport", country: "UK" },
  { city: "Birmingham", code: "BHX", name: "Birmingham Airport", country: "UK" },
  { city: "Bristol", code: "BRS", name: "Bristol Airport", country: "UK" },
  { city: "Leeds", code: "LBA", name: "Leeds Bradford", country: "UK" },
  { city: "Liverpool", code: "LPL", name: "John Lennon", country: "UK" },
  { city: "Newcastle", code: "NCL", name: "Newcastle Airport", country: "UK" },
];

const CityAutocomplete = ({ value, onChange, onSelect, placeholder = "Where are you flying from?" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Filter suggestions based on input
  useEffect(() => {
    if (value.length > 0) {
      const filtered = AIRPORTS.filter(
        (airport) =>
          airport.city.toLowerCase().includes(value.toLowerCase()) ||
          airport.code.toLowerCase().includes(value.toLowerCase()) ||
          airport.name.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 8);
      setSuggestions(filtered);
      setIsOpen(filtered.length > 0);
    } else {
      setSuggestions(AIRPORTS.slice(0, 6));
      setIsOpen(false);
    }
    setHighlightedIndex(0);
  }, [value]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "ArrowDown") {
        setIsOpen(true);
        setSuggestions(AIRPORTS.slice(0, 6));
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        if (suggestions[highlightedIndex]) {
          handleSelect(suggestions[highlightedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        break;
    }
  };

  const handleSelect = (airport) => {
    onSelect(airport.city, airport.code);
    setIsOpen(false);
  };

  // Group airports by city
  const groupedByCity = suggestions.reduce((acc, airport) => {
    if (!acc[airport.city]) {
      acc[airport.city] = [];
    }
    acc[airport.city].push(airport);
    return acc;
  }, {});

  return (
    <div ref={wrapperRef} className="relative" style={{ overflow: 'visible' }}>
      <div className="relative">
        <Plane className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary" />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => {
            setSuggestions(value ? suggestions : AIRPORTS.slice(0, 6));
            setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full h-12 pl-10 pr-4 glass-solid rounded-lg text-foreground placeholder:text-muted-foreground font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          data-testid="city-autocomplete-input"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
        />
      </div>

      {isOpen && suggestions.length > 0 && (
        <div 
          className="autocomplete-dropdown absolute w-full mt-2 glass-strong rounded-lg shadow-2xl border border-border/50"
          style={{ zIndex: 9999, position: 'absolute' }}
          data-dropdown="true"
        >
          <div className="max-h-72 overflow-y-auto overscroll-contain touch-pan-y">
            {Object.entries(groupedByCity).map(([city, airports], cityIndex) => (
              <div key={city}>
                {airports.length > 1 && (
                  <div className="px-4 py-2 text-xs text-muted-foreground uppercase tracking-wider bg-muted/30 sticky top-0">
                    {city} - All Airports
                  </div>
                )}
                {airports.map((airport, index) => {
                  const flatIndex = suggestions.findIndex((s) => s.code === airport.code);
                  const isHighlighted = flatIndex === highlightedIndex;
                  return (
                    <button
                      key={airport.code}
                      onClick={() => handleSelect(airport)}
                      onTouchEnd={(e) => { e.preventDefault(); handleSelect(airport); }}
                      className={`w-full px-4 py-4 flex items-center gap-3 transition-colors active:bg-primary/30 ${
                        isHighlighted ? "bg-primary/20" : "hover:bg-muted/50"
                      }`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-muted/50 flex items-center justify-center flex-shrink-0">
                        <span className="font-mono text-xs font-bold text-primary">{airport.code}</span>
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="font-medium text-foreground truncate">{airport.name}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{airport.city}, {airport.country}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CityAutocomplete;
